// scheduling/pages/EmployeeProfile.jsx
// Staff profile: info, experience scores, certifications, interests, skills, schedule.

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchStaff, fetchSchedule, fetchExperience, fetchExperienceRanking, fetchHolidays, fetchGlobalAssignments,
         fetchCertifications, fetchInterests, fetchSkills, fetchCalendars, fetchAssessments, fetchPTO } from '../services/schedData';
import { updateExperience, upsertCalendar, deleteCalendar, upsertInterest, requestPTO, cancelPTO, fetchLeaveBalance, upsertStaffDefaultTemplate } from '../services/schedApi';
import { fetchCapacityImpact } from '../services/schedData';
import { BRAND, EXP_LEVELS, getExpLevel, PI_PROFILES, PI_CATEGORY_COLORS } from '../utils/schedConstants';
import { toISO, makeGetDayCap, buildPtoByStaffDay, buildHolidayByDate, buildDayHoursByStaff, computeStaffWeeklyUtil, resolveEffectiveTemplate, isEmployedOn } from '../utils/capacity';
import NonBillProfilePanel from '../components/NonBillProfilePanel';
import ExperienceRankingList from '../components/ExperienceRankingList';
import ScheduleHistoryModal from './ScheduleHistoryModal';
import { useUrlState } from '../../hooks/useUrlState';

// All distinct niches from rprs.IndustryNicheMap
const ALL_NICHES = [
  'Agriculture, Forestry, and Fishing', 'BDO Engagements', 'Construction', 'Consumer Business',
  'EBP Audit', 'Estate & Trust', 'Film & Entertainment', 'Financial Institutions',
  'Government Contracting', 'Governmental & Municipalities', 'Healthcare', 'Individual',
  'Manufacturing & Distribution', 'Nonprofits', 'Oil & Gas', 'Private Equity & Venture Capital',
  'Professional Services', 'Real Estate', 'Technology & Software',
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_KEYS = ['MonHours', 'TueHours', 'WedHours', 'ThuHours', 'FriHours', 'SatHours', 'SunHours'];

function weeklyTotal(cal) {
  return DAY_KEYS.reduce((s, k) => s + (Number(cal[k]) || 0), 0);
}

function fmtRange(cal) {
  if (!cal.EffectiveStart && !cal.EffectiveEnd) return 'Year-round default';
  // Parse at local noon \u2014 a bare 'YYYY-MM-DD' parses as UTC midnight and renders the
  // prior day in US timezones (busy season showed "Jan 14" instead of Jan 15).
  const fmt = (v) => new Date(String(v).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const s = cal.EffectiveStart ? fmt(cal.EffectiveStart) : '...';
  const e = cal.EffectiveEnd ? fmt(cal.EffectiveEnd) : '...';
  return `${s} \u2013 ${e}`;
}

const DEPT_OPTIONS = [
  { code: '', label: 'All Departments' },
  { code: 'AUDIT', label: 'Audit & Assurance' },
  { code: 'TAX', label: 'Tax' },
  { code: 'GENERAL', label: 'General Track' },
  { code: 'CONSULT', label: 'Consulting Services' },
  { code: 'CAS', label: 'CAS' },
  { code: 'GOVCON', label: 'GovCon' },
  { code: 'ESTATE', label: 'Estate & Trust' },
];

function emptyTemplate(staffId, isGlobal) {
  return { StaffId: isGlobal ? null : staffId, TemplateName: '', EffectiveStart: '', EffectiveEnd: '',
    DepartmentCode: '', IsGlobal: !!isGlobal,
    MonHours: 8, TueHours: 8, WedHours: 8, ThuHours: 8, FriHours: 8, SatHours: 0, SunHours: 0,
    IsActive: true, Priority: 0 };
}

function levelColor(l) { return EXP_LEVELS[l]?.color || '#E5E7EB'; }

function fmtDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMonday() {
  const d = new Date();
  const dow = d.getDay();
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function ScorePips({ level, max = 5 }) {
  return (
    <div className="sched-emp-pips">
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} className="sched-emp-pip" style={{ background: i < level ? levelColor(level) : '#E5E7EB' }} />
      ))}
    </div>
  );
}

export default function EmployeeProfile() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { isSuperUser, username } = useAuth(); // P13: committee check

  const [staff, setStaff] = useState(null);
  const [experience, setExperience] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [utilization, setUtilization] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [skills, setSkills] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  // Person-mode schedule history (every change to this person's schedule, any job).
  const [showHistory, setShowHistory] = useState(false);
  // Experience sub-pill lives in the URL (?subtab=) for deeplinks.
  const [expTab, setExpTab] = useUrlState('subtab', 'client', { validate: ['client', 'niche', 'serviceline'] });
  const [expSearch, setExpSearch] = useState('');

  // Experience ranking modal
  const [rankingModal, setRankingModal] = useState(null);
  const [rankingData, setRankingData] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);

  // P12: Manual ranking state
  const [showAddRanking, setShowAddRanking] = useState(false);
  const [rankDimName, setRankDimName] = useState('');
  const [rankLevel, setRankLevel] = useState(3);
  const [rankNote, setRankNote] = useState('');
  const [rankSaving, setRankSaving] = useState(false);
  const [ptoData, setPtoData] = useState([]);
  const [ptoFormOpen, setPtoFormOpen] = useState(false);
  const [ptoForm, setPtoForm] = useState({ startDate: '', endDate: '', notes: '' });
  const [ptoSubmitting, setPtoSubmitting] = useState(false);
  const [ptoCancelling, setPtoCancelling] = useState(null); // leaveReqId being cancelled
  const [ptoBalance, setPtoBalance] = useState(null);
  const [ptoBalanceLoading, setPtoBalanceLoading] = useState(false);
  // PTO status filter lives in the URL (?status=) for deeplinks.
  const [ptoStatusFilter, setPtoStatusFilter] = useUrlState('status', 'all', {
    validate: ['all', 'pending', 'approved', 'rejected', 'cancelled'],
  });
  // Active tab lives in the URL (?tab=) for shareable / navbot deeplinks; the
  // hook handles two-way binding + back/forward automatically. Valid keys must
  // match the tab list in the render below.
  const VALID_TABS = ['experience', 'capacity', 'pto', 'nonbill', 'profile', 'schedule'];
  const [profileTab, setProfileTab] = useUrlState('tab', 'experience', { validate: VALID_TABS });
  // One-time migration of legacy `#nonbill` hash bookmarks → `?tab=nonbill`.
  useEffect(() => {
    const fromHash = (window.location.hash || '').replace(/^#/, '').toLowerCase();
    if (VALID_TABS.includes(fromHash)) {
      setProfileTab(fromHash);
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [calendars, setCalendars] = useState([]);
  const [calEditing, setCalEditing] = useState(false);
  const [editingCal, setEditingCal] = useState(null); // template being edited/created
  const [calSaving, setCalSaving] = useState(false);

  // Niche preferences (ranked top 5)
  const [nichePrefs, setNichePrefs] = useState([]); // [{ interestTag, rank }] rank 1-5
  const [nicheSaving, setNicheSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setPhotoFailed(false);
      setError(null);
      const sid = parseInt(staffId);
      const monday = getMonday();
      const endDate = new Date(monday);
      endDate.setDate(endDate.getDate() + 27);
      const startISO = monday.toISOString().slice(0, 10);
      const endISO = endDate.toISOString().slice(0, 10);

      try {
        const [staffData, expData, schedData, holidayData, gaData, certData, intData, skillData, calData, assessData, ptoResult] = await Promise.all([
          // includeScheduledInactive: the grid links departed-but-still-scheduled staff
          // here by name, and without the flag this page can't find them (the lookup
          // below falls through to null → "Staff Not Found").
          fetchStaff({ includeScheduledInactive: true }),
          fetchExperience(sid),
          fetchSchedule({ staffId: sid, startDate: startISO, endDate: endISO }),
          fetchHolidays(),
          fetchGlobalAssignments(),
          fetchCertifications(sid),
          fetchInterests(sid),
          fetchSkills(sid),
          fetchCalendars(sid),
          fetchAssessments(sid),
          fetchPTO({ staffId: sid }),
        ]);
        if (cancelled) return;
        setStaff((staffData || []).find(s => s.StaffId === sid) || null);
        setExperience(expData || []);
        setSchedule(schedData || []);
        // Utilization computed with the shared capacity util (same as the grid) —
        // PTO/holiday/global-assignment aware, over the 4-week window.
        {
          const weekMondays = [];
          for (let i = 0; i < 4; i++) { const d = new Date(monday); d.setDate(d.getDate() + i * 7); weekMondays.push(toISO(d)); }
          const years = new Set([monday.getFullYear(), endDate.getFullYear()]);
          const getDayCap = makeGetDayCap({
            staff: staffData || [],
            globalCalendars: calData || [],
            ptoByStaffDay: buildPtoByStaffDay(ptoResult),
            holidayByDate: buildHolidayByDate(holidayData, years),
          });
          const dayHoursByStaff = buildDayHoursByStaff(schedData, gaData, staffData);
          const rows = computeStaffWeeklyUtil({ staffIds: [sid], weekMondays, getDayCap, dayHoursByStaff });
          setUtilization(rows[0]?.weeks || []);
        }
        setCertifications(certData || []);
        const ranked = (intData || [])
          .filter(i => i.Rank != null)
          .sort((a, b) => a.Rank - b.Rank)
          .map(i => ({ interestTag: i.InterestTag, rank: i.Rank }));
        setNichePrefs(ranked);
        setSkills(skillData || []);
        setCalendars(calData || []);
        setAssessments(assessData || []);
        setPtoData(ptoResult || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [staffId]);

  // Fetch leave balance when PTO tab opens
  useEffect(() => {
    if (profileTab !== 'pto' || !staffId) return;
    setPtoBalanceLoading(true);
    fetchLeaveBalance(parseInt(staffId))
      .then(data => setPtoBalance(data))
      .catch(() => setPtoBalance(null))
      .finally(() => setPtoBalanceLoading(false));
  }, [profileTab, staffId]);

  // Group experience by dimension (niche replaces industry)
  const expByDim = useMemo(() => {
    const map = { client: [], niche: [], serviceline: [] };
    // Exclusions: BMSS internal entities from client/niche, Firm Admin/PTO from service line
    const EXCLUDE_NICHE = new Set(['bmss', 'bdo engagements']);
    const EXCLUDE_SERVICE = new Set(['firm - admin', 'firm - pto', 'firm - operations', 'firm admin', 'pto']);
    // BMSS's own entities (the firm + its LLCs: BMSS AI, BMSS Business Services, BMSS Ad Valorem,
    // BMSS Strategic Services, BMSS Healthcare Advisory, etc.) — exclude from the Client sub-tab.
    const isBmssClient = (name) => name === 'bmss' || name.startsWith('bmss ') || name.startsWith('bmss,');
    // PTO / firm-admin service lines (catch near-miss values like "Firm - Paid Time Off").
    const isExcludedService = (name) => EXCLUDE_SERVICE.has(name) || name.includes('pto') || name.startsWith('firm -');
    for (const e of experience) {
      let dim = (e.Dimension || '').toLowerCase();
      const name = (e.DimensionName || '').toLowerCase();
      // Legacy: treat 'industry' as 'niche' for backwards compat
      if (dim === 'industry') dim = 'niche';
      if (dim === 'client' && isBmssClient(name)) continue;
      if (dim === 'niche' && EXCLUDE_NICHE.has(name)) continue;
      if (dim === 'serviceline' && isExcludedService(name)) continue;
      if (map[dim]) map[dim].push(e);
    }
    // Dedupe each dimension by its grain key, keeping the highest-hours representative.
    // Stale syncs left duplicate rows that share a display name (client: distinct ContIndexes;
    // niche/serviceline: distinct PE codes) all carrying the SAME already-aggregated hours — so
    // we dedupe, NOT sum (summing would N-times-inflate, e.g. 10×1433h). Grain matches the
    // backend + consumers: client by DimensionId (ContIndex), niche/serviceline by DimensionName
    // (blank → "Unknown"). A clean DB (migrate-051 + re-sync) makes this a no-op safety net.
    const dedupeBy = (rows, keyFn) => {
      const seen = new Map();
      for (const e of rows) {
        const key = keyFn(e);
        const prev = seen.get(key);
        if (!prev || (e.TotalHours || 0) > (prev.TotalHours || 0)) seen.set(key, e);
      }
      return [...seen.values()];
    };
    const nameKey = (e) => (e.DimensionName || '').trim().toLowerCase() || 'unknown';
    map.client = dedupeBy(map.client, e => e.DimensionId);
    map.serviceline = dedupeBy(map.serviceline, nameKey);
    map.niche = dedupeBy(map.niche, nameKey).map(e =>
      (e.DimensionName || '').trim() ? e : { ...e, DimensionName: 'Unknown' }
    );
    // Sort by percentile (descending), fallback to level (merged Unknown row has neither → sinks to bottom)
    for (const k of Object.keys(map)) map[k].sort((a, b) => (b.Percentile || b.Level || 0) - (a.Percentile || a.Level || 0));
    return map;
  }, [experience]);

  // Schedule summary
  const upcomingJobs = useMemo(() => {
    const map = {};
    for (const e of schedule) {
      if (!map[e.JobId]) map[e.JobId] = { ...e, totalHours: 0 };
      map[e.JobId].totalHours += e.Hours || 0;
    }
    return Object.values(map).sort((a, b) => b.totalHours - a.totalHours);
  }, [schedule]);

  const avgUtil = useMemo(() => {
    if (!utilization.length) return 0;
    return Math.round(utilization.reduce((s, u) => s + (u.pct || 0), 0) / utilization.length);
  }, [utilization]);

  const totalHours = schedule.reduce((s, e) => s + (e.Hours || 0), 0);

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading profile...</div></div>;
  if (error) return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;
  if (!staff) return <div className="sched-placeholder"><h2>Staff Not Found</h2></div>;

  const initials = (staff.Initials || staff.Name?.split(' ').map(w => w[0]).join('') || '??').toUpperCase().slice(0, 2);
  const isMe = staff.Email && username && staff.Email.toLowerCase() === (username + '@bmss.com').toLowerCase();
  const photoUrl = isMe ? `/api/myPhoto?size=96x96&u=${encodeURIComponent(staff.Email)}` : null;

  return (
    <div className="sched-emp-root">
      {/* Header */}
      <div className="sched-emp-header">
        <button className="sched-sug-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="sched-emp-avatar-lg" style={{ background: BRAND.deepTeal, overflow: 'hidden' }}>
          {photoUrl && !photoFailed ? (
            <img src={photoUrl} alt="" onError={() => setPhotoFailed(true)}
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
          ) : initials}
        </div>
        <div className="sched-emp-header-info">
          <h2 className="sched-emp-name">{staff.Name}</h2>
          <div className="sched-emp-meta">
            {staff.Level} &middot; {staff.Department || staff.DepartmentCode} &middot; {staff.Office || staff.OfficeCode}
          </div>
          <div className="sched-emp-meta">{staff.Email}</div>
          <button type="button" className="sched-mgr-pill" onClick={() => setShowHistory(true)}
            style={{ marginTop: 6, padding: '3px 10px', fontSize: 11 }}
            title="Every change to this person's schedule — client jobs, internal jobs and placeholders">
            &#128203; View Schedule History
          </button>
        </div>
        <div className="sched-emp-header-stats">
          <div className="sched-emp-header-stat">
            <div className="sched-emp-header-stat-value" style={{ color: BRAND.deepTeal }}>{Math.round(totalHours)}h</div>
            <div className="sched-emp-header-stat-label">Next 4 Weeks</div>
          </div>
          <div className="sched-emp-header-stat">
            <div className="sched-emp-header-stat-value" style={{ color: avgUtil >= 90 ? '#EF4444' : avgUtil >= 75 ? '#F59E0B' : '#10B981' }}>{avgUtil}%</div>
            <div className="sched-emp-header-stat-label">Utilization</div>
          </div>
          <div className="sched-emp-header-stat">
            <div className="sched-emp-header-stat-value" style={{ color: BRAND.oceanBlue }}>{upcomingJobs.length}</div>
            <div className="sched-emp-header-stat-label">Active Jobs</div>
          </div>
          <div className="sched-emp-header-stat">
            <div className="sched-emp-header-stat-value" style={{ color: BRAND.sageGreen }}>{experience.length}</div>
            <div className="sched-emp-header-stat-label">Exp. Scores</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sched-tabs">
        {[['experience', 'Experience'], ['capacity', 'Capacity'], ['pto', 'PTO'], ['nonbill', 'Non-Billable Hours'], ['profile', 'Profile'], ['schedule', 'Schedule']].map(([key, label]) => (
          <button key={key} data-tour={`emp-tab-${key}`} className={`sched-tab ${profileTab === key ? 'sched-tab--active' : ''}`}
            onClick={() => setProfileTab(key)}>{label}</button>
        ))}
      </div>

      <div className="sched-emp-body">

        {/* ── Experience Tab ── */}
        {profileTab === 'experience' && (
          <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
            {/* P13: Info banner for non-committee */}
            {!isSuperUser && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1D4ED8', marginBottom: 12 }}>
                Experience scores are calculated automatically from PE time entries and updated on sync.
              </div>
            )}

            <h3 className="sched-emp-panel-title">Experience Scores</h3>
            <div className="sched-emp-exp-tabs">
              {[['client', `Client (${expByDim.client.length})`],
                ['niche', `Niche (${expByDim.niche.length})`],
                ['serviceline', `Service Line (${expByDim.serviceline.length})`]].map(([key, label]) => (
                <button key={key} className={`sched-mgr-pill${expTab === key ? ' sched-mgr-pill--active' : ''}`}
                  onClick={() => { setExpTab(key); setExpSearch(''); }}>{label}</button>
              ))}
            </div>

            <input
              type="text"
              placeholder="Search..."
              value={expSearch}
              onChange={e => setExpSearch(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }}
            />

            <div className="sched-emp-exp-list">
              {(() => {
                const rows = (expByDim[expTab] || []).filter(e =>
                  !expSearch || e.DimensionName.toLowerCase().includes(expSearch.toLowerCase())
                );
                if (rows.length === 0) return (
                  <div className="sched-emp-exp-empty">
                    {expSearch ? 'No matches.' : `No experience data.${experience.length === 0 ? ' Run the experience seed to populate from PE WIP data.' : ''}`}
                  </div>
                );
                return rows.map((e, i) => {
                  const expLevel = getExpLevel(e.Percentile);
                  const topPct = e.Percentile != null ? Math.max(1, Math.round(100 - e.Percentile)) : null;
                  return (
                    <div key={e.ScoreId || i} className={`sched-emp-exp-row${e.IsManualOverride ? ' sched-emp-exp-row--override' : ''}`}>
                      <div className="sched-emp-exp-name" style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setRankingModal({ dimension: e.Dimension, dimensionName: e.DimensionName, dimensionId: e.DimensionId });
                          setRankingLoading(true);
                          fetchExperienceRanking(e.Dimension, e.DimensionName, e.DimensionId)
                            .then(data => setRankingData(data || []))
                            .finally(() => setRankingLoading(false));
                        }}
                        title="View staff rankings for this dimension">
                        <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{e.DimensionName}</span>
                        {e.IsManualOverride && <span style={{ fontSize: 10, color: '#D97706', marginLeft: 4 }}>&#9998; Manual</span>}
                      </div>
                      <ScorePips level={e.Level || 0} />
                      <span className="sched-emp-exp-level" style={{ color: expLevel.color, minWidth: 72 }}>
                        {expLevel.label}
                      </span>
                      <span className="sched-emp-exp-hours">{Math.round(e.TotalHours || 0)}h</span>
                      {topPct != null && e.PopSize >= 5
                        ? <span style={{ fontSize: 10, color: '#9CA3AF', minWidth: 90, textAlign: 'right' }}>
                            top {topPct}% of {e.PopSize}
                          </span>
                        : topPct != null && e.PopSize > 0
                          ? <span style={{ fontSize: 10, color: '#9CA3AF', minWidth: 90, textAlign: 'right', fontStyle: 'italic' }}>
                              top {topPct}% globally
                            </span>
                          : null
                      }
                    </div>
                  );
                });
              })()}
            </div>

            {/* P12: Manual Ranking (committee only) */}
            {isSuperUser && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280' }}>&#9998; Manual Rankings</span>
                  {!showAddRanking && (
                    <button className="sched-mgr-pill" style={{ fontSize: 11 }}
                      onClick={() => setShowAddRanking(true)}>+ Add Ranking</button>
                  )}
                </div>

                {showAddRanking && (
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div className="sched-mgr-modal-field" style={{ marginBottom: 8 }}>
                      <label className="sched-mgr-modal-label">Description</label>
                      <input type="text" placeholder="e.g. Enterprise Audit Engagements"
                        value={rankDimName} onChange={e => setRankDimName(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }} />
                    </div>
                    <div className="sched-mgr-modal-field" style={{ marginBottom: 8 }}>
                      <label className="sched-mgr-modal-label">Level (1-5)</label>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[1, 2, 3, 4, 5].map(l => (
                          <button key={l} onClick={() => setRankLevel(l)}
                            style={{
                              width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
                              background: rankLevel === l ? levelColor(l) : '#E5E7EB',
                              color: rankLevel === l ? '#fff' : '#6B7280',
                              fontWeight: 700, fontSize: 13,
                            }}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div className="sched-mgr-modal-field" style={{ marginBottom: 8 }}>
                      <label className="sched-mgr-modal-label">Note (optional)</label>
                      <input type="text" placeholder="e.g. Prior firm experience"
                        value={rankNote} onChange={e => setRankNote(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="sched-assign-btn" style={{ fontSize: 12, padding: '6px 14px' }}
                        disabled={!rankDimName.trim() || rankSaving}
                        onClick={async () => {
                          setRankSaving(true);
                          try {
                            await updateExperience({
                              staffId: parseInt(staffId),
                              dimension: 'manual',
                              dimensionName: rankDimName.trim(),
                              totalHours: 0,
                              level: rankLevel,
                              isManualOverride: true,
                              overrideNote: rankNote.trim() || null,
                              overrideBy: username,
                            });
                            // Refresh experience
                            const expData = await fetchExperience(parseInt(staffId));
                            setExperience(expData || []);
                            setShowAddRanking(false);
                            setRankDimName('');
                            setRankLevel(3);
                            setRankNote('');
                          } catch (e) {
                            alert('Save failed: ' + e.message);
                          } finally {
                            setRankSaving(false);
                          }
                        }}>
                        {rankSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button className="sched-mgr-pill" style={{ fontSize: 12 }}
                        onClick={() => { setShowAddRanking(false); setRankDimName(''); setRankNote(''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Capacity Tab (Calendar Templates — Global + Per-Staff) ── */}
        {profileTab === 'capacity' && (() => {
          const globalCals = calendars.filter(c => c.IsGlobal);
          const customCals = calendars.filter(c => !c.IsGlobal);
          // Only ACTIVE overrides can override anything. fetchCalendars deliberately
          // returns deactivated rows (the PE sync deactivates rather than deletes, so
          // admins need to see them), and counting those here made the page claim a
          // staffer had overrides when every one of them was switched off — no template
          // got the IN EFFECT badge and the banner below insisted the globals didn't
          // apply when in fact they were the only thing applying.
          const hasCustom = customCals.some(c => c.IsActive);

          // Which template is in effect TODAY. Delegated to the shared resolver in
          // utils/capacity.js so this badge and the grid can never disagree again — they
          // previously used different tiebreaks, so a staffer could read 6h/day here
          // while the grid scheduled them at 8h/day.
          const todayISO = toISO(new Date());
          const firmDefaultId = globalCals.find(c => c.IsDefault)?.TemplateId;
          const employedToday = isEmployedOn(staff, todayISO);
          const resolved = resolveEffectiveTemplate({
            person: staff, templates: calendars, dateISO: todayISO, firmDefaultId,
          });
          // Not employed today (a Sept start, or departed) means nothing is in effect at
          // all — capacity is 0 regardless of which template would otherwise win.
          const currentTemplateId = employedToday ? (resolved.template?.TemplateId ?? null) : null;

          // Render a single calendar card
          const renderCard = (cal, canEdit) => {
            const isCurrent = cal.TemplateId === currentTemplateId;
            // A global is "overridden" only when an ACTIVE per-staff row actually beat it.
            const isOverridden = cal.IsGlobal && currentTemplateId != null && !isCurrent
              && resolved.template?.StaffId != null;
            return (
            <div key={cal.TemplateId} className={`sched-cal-card${isCurrent && !isOverridden ? ' sched-cal-card--active' : ''}`}>
              <div className="sched-cal-card-header">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {isCurrent && !isOverridden && <span className="sched-cal-active-badge">IN EFFECT</span>}
                    {!cal.IsActive && <span className="sched-cal-disabled-badge">DISABLED</span>}
                    <span className={`sched-cal-type-badge${cal.IsGlobal ? '' : ' sched-cal-type-badge--custom'}`}>
                      {cal.IsGlobal ? 'GLOBAL' : 'CUSTOM'}
                    </span>
                    {/* Trello #332 — where this row's hours come from, so it's obvious
                        which rows the PE reconcile will overwrite and which are ours. */}
                    {cal.SyncScope === 'pe-staff' && (
                      <span className="sched-cal-scope-badge sched-cal-scope-badge--pe"
                        title="Mirrors this person's Practice Engine work profile. The PE sync keeps it up to date — edits here will be overwritten unless PE changes too.">
                        ⇄ PE
                      </span>
                    )}
                    {cal.SyncScope === 'pe-profile' && (
                      <span className="sched-cal-scope-badge sched-cal-scope-badge--pe"
                        title="Mirrors a firm-wide Practice Engine work profile. Editing its hours changes capacity for everyone assigned to it and diverges from PE's definition.">
                        ⇄ PE profile
                      </span>
                    )}
                    {cal.SyncScope === 'rprs-native' && (
                      <span className="sched-cal-scope-badge sched-cal-scope-badge--local"
                        title="RPRS-only — seasonal uplift, department-scoped, or end-dated. Practice Engine has no equivalent, so the PE sync never touches it.">
                        RPRS only
                      </span>
                    )}
                    <span className="sched-cal-name">{cal.TemplateName}</span>
                  </div>
                  <div className="sched-cal-meta">
                    Effective: {fmtRange(cal)} &middot; {weeklyTotal(cal)}h/week
                    {cal.DepartmentCode && <span style={{ marginLeft: 6 }}>({cal.DepartmentCode})</span>}
                    {cal.Priority > 0 && <span style={{ marginLeft: 6, color: '#9CA3AF' }}>P{cal.Priority}</span>}
                  </div>
                </div>
                {canEdit && calEditing && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="sched-cal-small-btn"
                      onClick={() => setEditingCal({ ...cal, IsGlobal: !!cal.IsGlobal })}>Edit</button>
                    {/* Enable / Disable. Without this there was no way OUT of the disabled
                        state from the UI: the PE sync deactivates a superseded override
                        rather than deleting it, disabled rows are excluded from the Default
                        Template picker (a disabled template can't apply, so offering it
                        would be a no-op), and the only other control was Delete. Admins
                        could see a schedule they were unable to restore. */}
                    {!cal.IsGlobal && (
                      <button className="sched-cal-small-btn"
                        title={cal.IsActive
                          ? 'Disable this override — keeps it on record but stops it applying'
                          : 'Re-enable this override so it can apply again'}
                        onClick={async () => {
                          const turningOn = !cal.IsActive;
                          if (turningOn && !window.confirm(
                            `Re-enable "${cal.TemplateName}" (${weeklyTotal(cal)}h/week)?\n\n`
                            + `It will compete with this staff member's other templates by priority. `
                            + `If it wins, their capacity changes to these hours.\n\n`
                            + `Note: this does NOT change Practice Engine, so it will show as `
                            + `drift in Admin → Sync & Data until PE matches.`)) return;
                          try {
                            await upsertCalendar({
                              templateId: cal.TemplateId,
                              templateName: cal.TemplateName,
                              departmentCode: cal.DepartmentCode || null,
                              effectiveStart: cal.EffectiveStart || null,
                              effectiveEnd: cal.EffectiveEnd || null,
                              monHours: cal.MonHours, tueHours: cal.TueHours, wedHours: cal.WedHours,
                              thuHours: cal.ThuHours, friHours: cal.FriHours,
                              satHours: cal.SatHours, sunHours: cal.SunHours,
                              recurringAnnually: !!cal.RecurringAnnually,
                              priority: cal.Priority || 0,
                              isActive: turningOn,
                            });
                            const updated = await fetchCalendars(parseInt(staffId));
                            setCalendars(updated || []);
                          } catch (e) {
                            alert(`Could not ${turningOn ? 'enable' : 'disable'} template: ${e.message}`);
                          }
                        }}>
                        {cal.IsActive ? 'Disable' : 'Enable'}
                      </button>
                    )}
                    <button className="sched-cal-small-btn sched-cal-small-btn--danger"
                      onClick={async () => {
                        if (!window.confirm(`Delete "${cal.TemplateName}"?`)) return;
                        try {
                          await deleteCalendar(cal.TemplateId, parseInt(staffId));
                          const updated = await fetchCalendars(parseInt(staffId));
                          setCalendars(updated || []);
                        } catch (e) {
                          // Surfaces the new dependant-staff 409 from deleteCalendar rather
                          // than failing silently.
                          alert(e.message);
                        }
                      }}>Delete</button>
                  </div>
                )}
              </div>
              <div className="sched-cal-days">
                {DAYS.map((d, i) => {
                  const hrs = Number(cal[DAY_KEYS[i]]) || 0;
                  return (
                    <div key={d} className="sched-cal-day">
                      <div className="sched-cal-day-label">{d}</div>
                      <div className={`sched-cal-day-value${hrs > 0 ? ' sched-cal-day-value--on' : ''}`}>
                        {hrs > 0 ? hrs + 'h' : '\u2014'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
          };

          // Render the edit/create form
          const renderForm = () => editingCal && (
            <div className="sched-cal-form">
              <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#111827' }}>
                {editingCal.TemplateId ? 'Edit Template' : 'New Template'}
                {editingCal.IsGlobal ? ' (Global)' : ` (Custom for ${staff.Name})`}
              </h4>
              <div className="sched-cal-form-row">
                <label className="sched-mgr-modal-label">Name</label>
                <input type="text" value={editingCal.TemplateName}
                  onChange={e => setEditingCal({ ...editingCal, TemplateName: e.target.value })}
                  placeholder="e.g. Busy Season — Audit 51h"
                  className="sched-cal-input" />
              </div>
              {editingCal.IsGlobal && (
                <div className="sched-cal-form-row">
                  <label className="sched-mgr-modal-label">Department Scope</label>
                  <select value={editingCal.DepartmentCode || ''}
                    onChange={e => setEditingCal({ ...editingCal, DepartmentCode: e.target.value || null })}
                    className="sched-cal-input">
                    {DEPT_OPTIONS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                  </select>
                </div>
              )}
              <div className="sched-cal-form-row" style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label className="sched-mgr-modal-label">Effective Start</label>
                  <input type="date" value={editingCal.EffectiveStart ? editingCal.EffectiveStart.slice(0, 10) : ''}
                    onChange={e => setEditingCal({ ...editingCal, EffectiveStart: e.target.value || null })}
                    className="sched-cal-input" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="sched-mgr-modal-label">Effective End</label>
                  <input type="date" value={editingCal.EffectiveEnd ? editingCal.EffectiveEnd.slice(0, 10) : ''}
                    onChange={e => setEditingCal({ ...editingCal, EffectiveEnd: e.target.value || null })}
                    className="sched-cal-input" />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                Leave dates blank for a year-round default template.
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', cursor: 'pointer', marginBottom: 10 }}>
                <input type="checkbox" checked={!!editingCal.RecurringAnnually}
                  onChange={e => setEditingCal({ ...editingCal, RecurringAnnually: e.target.checked })} />
                Recurs annually
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>(match the dates by month/day every year)</span>
              </label>
              <div className="sched-cal-form-row">
                <label className="sched-mgr-modal-label">Hours per Day</label>
                <div className="sched-cal-days" style={{ marginTop: 4 }}>
                  {DAYS.map((d, i) => (
                    <div key={d} className="sched-cal-day">
                      <div className="sched-cal-day-label">{d}</div>
                      <input type="number" min="0" max="24" step="0.5"
                        value={editingCal[DAY_KEYS[i]] ?? 0}
                        onChange={e => setEditingCal({ ...editingCal, [DAY_KEYS[i]]: parseFloat(e.target.value) || 0 })}
                        className="sched-cal-day-input" />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: BRAND.deepTeal, fontWeight: 600, marginTop: 6 }}>
                  Weekly total: {weeklyTotal(editingCal)}h
                </div>
              </div>
              <div className="sched-cal-form-row" style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label className="sched-mgr-modal-label">Priority (higher wins on overlap)</label>
                  <input type="number" min="0" max="99"
                    value={editingCal.Priority ?? 0}
                    onChange={e => setEditingCal({ ...editingCal, Priority: parseInt(e.target.value) || 0 })}
                    className="sched-cal-input" style={{ width: 80 }} />
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editingCal.IsActive !== false}
                      onChange={e => setEditingCal({ ...editingCal, IsActive: e.target.checked })} />
                    Active
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="sched-assign-btn" style={{ fontSize: 12, padding: '6px 16px' }}
                  disabled={!editingCal.TemplateName.trim() || calSaving}
                  onClick={async () => {
                    const payload = {
                      templateId: editingCal.TemplateId || undefined,
                      staffId: editingCal.IsGlobal ? null : parseInt(staffId),
                      departmentCode: editingCal.DepartmentCode || null,
                      templateName: editingCal.TemplateName.trim(),
                      effectiveStart: editingCal.EffectiveStart || null,
                      effectiveEnd: editingCal.EffectiveEnd || null,
                      recurringAnnually: !!editingCal.RecurringAnnually,
                      monHours: editingCal.MonHours, tueHours: editingCal.TueHours,
                      wedHours: editingCal.WedHours, thuHours: editingCal.ThuHours,
                      friHours: editingCal.FriHours, satHours: editingCal.SatHours,
                      sunHours: editingCal.SunHours,
                      isActive: editingCal.IsActive !== false,
                      priority: editingCal.Priority || 0,
                      createdBy: username,
                    };

                    // ── Confirm-gate (Trello #332 Phase 3) ────────────────────────
                    // A capacity edit that reaches PE is not a scheduling-only change:
                    // PE derives PTO hours-per-day from the work profile, so already-
                    // approved time off gets revalued. Name the consequence, name who is
                    // affected, and surface the PTO at stake before writing anything.
                    if (payload.isActive) {
                      let impact = null;
                      try { impact = await fetchCapacityImpact(payload); } catch { /* fall through un-gated rather than block the save */ }
                      if (impact?.willReachPE && impact.affected.length) {
                        const names = impact.affected.map(a => a.name);
                        const shown = names.slice(0, 10).join(', ') + (names.length > 10 ? `, +${names.length - 10} more` : '');
                        const pto = impact.approvedPTO || [];
                        const ok = window.confirm(
                          `This will update Practice Engine.\n\n`
                          + `New hours: ${[payload.monHours, payload.tueHours, payload.wedHours, payload.thuHours, payload.friHours].join('/')}`
                          + `${Number(payload.satHours) ? '/' + payload.satHours : ''}${Number(payload.sunHours) ? '/' + payload.sunHours : ''}\n`
                          + `${impact.fanOut ? `Affects ${names.length} staff who use this template: ` : 'Affects: '}${shown}\n\n`
                          + `Practice Engine uses the work profile to value approved PTO, so time off already booked will be recalculated.`
                          + (pto.length
                            ? `\n\nApproved PTO in range:\n${pto.slice(0, 8).map(p => `  • ${p.name}: ${p.days} day(s), ${p.hours}h`).join('\n')}`
                              + (pto.length > 8 ? `\n  • +${pto.length - 8} more` : '')
                            : `\n\nNo approved PTO in range.`)
                          + `\n\nContinue?`);
                        if (!ok) return;
                      } else if (impact?.blocker) {
                        // Not a blocker for the save — just an honest heads-up that PE
                        // will NOT be told, so the two will read differently.
                        const ok = window.confirm(
                          `This change stays in RPRS only.\n\n${impact.blocker}\n\n`
                          + `Practice Engine will not be updated, so RPRS and PE will differ for this schedule.\n\nContinue?`);
                        if (!ok) return;
                      }
                    }

                    setCalSaving(true);
                    try {
                      const saved = await upsertCalendar(payload);
                      const updated = await fetchCalendars(parseInt(staffId));
                      setCalendars(updated || []);
                      // Creating a per-staff override makes it the staffer's default
                      // server-side. Mirror that into local state or the Default Template
                      // picker keeps showing the OLD template until a page refresh.
                      if (saved?.defaultRepointedTo) {
                        setStaff(s => s ? { ...s, DefaultCalendarTemplateId: saved.defaultRepointedTo } : s);
                      }
                      setEditingCal(null);
                    } catch (e) {
                      alert('Save failed: ' + e.message);
                    } finally {
                      setCalSaving(false);
                    }
                  }}>
                  {calSaving ? 'Saving...' : 'Save Template'}
                </button>
                <button className="sched-mgr-pill" style={{ fontSize: 12 }}
                  onClick={() => setEditingCal(null)}>Cancel</button>
              </div>
            </div>
          );

          return (
            <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 className="sched-emp-panel-title" style={{ margin: 0 }}>Schedule & Capacity</h3>
                {isSuperUser && (
                  <button className="sched-cal-edit-btn"
                    style={{ background: calEditing ? BRAND.brightGreen : BRAND.deepTeal,
                             color: calEditing ? BRAND.deepTeal : '#fff' }}
                    onClick={() => { setCalEditing(!calEditing); setEditingCal(null); }}>
                    {calEditing ? 'Done Editing' : 'Edit Calendars'}
                  </button>
                )}
              </div>

              {!isSuperUser && (
                <div className="sched-cal-info-banner">
                  Calendar templates are managed by the Production Committee. Contact them to request changes.
                </div>
              )}

              {/* Item 9 — Default Template picker. This staffer's default participates
                  in the priority pool alongside dept-scoped globals; busy-season globals
                  with higher Priority will still take precedence within their date range. */}
              {(() => {
                const defaultId = staff?.DefaultCalendarTemplateId || null;
                const defaultCal = defaultId
                  ? calendars.find(c => c.TemplateId === defaultId)
                  : null;
                // Pickable: ACTIVE globals + this staffer's own active overrides. Disabled
                // rows are excluded on purpose — a disabled template never applies, so
                // making it the default would silently do nothing. The hint below tells
                // the admin how to get one back instead of leaving them to wonder why a
                // schedule they can see isn't in the list.
                const pickable = calendars
                  .filter(c => c.IsActive && (c.IsGlobal || c.StaffId === parseInt(staffId)))
                  .sort((a, b) => {
                    // Group globals first by name, then per-staff overrides
                    if (a.IsGlobal !== b.IsGlobal) return a.IsGlobal ? -1 : 1;
                    return (a.TemplateName || '').localeCompare(b.TemplateName || '');
                  });
                const disabledCustoms = calendars.filter(c => !c.IsGlobal && !c.IsActive).length;
                const hours = defaultCal
                  ? `${defaultCal.MonHours}/${defaultCal.TueHours}/${defaultCal.WedHours}/${defaultCal.ThuHours}/${defaultCal.FriHours}` +
                    (Number(defaultCal.SatHours) > 0 ? `/${defaultCal.SatHours}` : '') +
                    (Number(defaultCal.SunHours) > 0 ? `/${defaultCal.SunHours}` : '') + 'h'
                  : null;
                return (
                  <div data-tour="capacity-default" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#065F46', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Default Template
                      </span>
                      {defaultCal ? (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                          {defaultCal.TemplateName}
                          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: '#6B7280' }}>{hours}</span>
                          {!defaultCal.IsGlobal && (
                            <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: '#FEF3C7', color: '#92400E', letterSpacing: 0.3 }}>
                              CUSTOM
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>None set</span>
                      )}
                      <div style={{ flex: 1 }} />
                      {isSuperUser && (
                        <select
                          value={defaultId || ''}
                          onChange={async e => {
                            const v = e.target.value;
                            const tid = v === '' ? null : parseInt(v);

                            // Confirm-gate (Trello #332). Assigning a different template is
                            // a contract change and now reaches Practice Engine, which
                            // derives PTO hours-per-day from the work profile — so booked
                            // time off gets revalued. Same gate as the hours-edit path.
                            if (tid != null) {
                              const target = calendars.find(c => c.TemplateId === tid);
                              let impact = null;
                              try {
                                impact = await fetchCapacityImpact({
                                  templateId: tid, staffId: parseInt(staffId),
                                  departmentCode: target?.DepartmentCode || null,
                                  recurringAnnually: !!target?.RecurringAnnually,
                                  effectiveStart: target?.EffectiveStart || null,
                                  effectiveEnd: target?.EffectiveEnd || null,
                                });
                              } catch { /* fall through un-gated rather than block the change */ }

                              const hrs = target
                                ? [target.MonHours, target.TueHours, target.WedHours, target.ThuHours, target.FriHours].join('/')
                                : '?';
                              const pto = impact?.approvedPTO || [];
                              const msg = impact?.willReachPE
                                ? `Change ${staff?.Name || 'this staff member'} to "${target?.TemplateName}" (${hrs})?\n\n`
                                  + `This updates BOTH RPRS capacity and Practice Engine — PE gets a new work-profile row `
                                  + `starting ${String(target?.EffectiveStart || '').slice(0, 10) || 'today'}.\n\n`
                                  + `PE values approved PTO from the work profile, so time off already booked will be recalculated.`
                                  + (pto.length
                                    ? `\n\nApproved PTO in range:\n${pto.slice(0, 8).map(p => `  • ${p.name}: ${p.days} day(s), ${p.hours}h`).join('\n')}`
                                    : `\n\nNo approved PTO in range.`)
                                  + `\n\nContinue?`
                                : `Change ${staff?.Name || 'this staff member'} to "${target?.TemplateName}" (${hrs})?\n\n`
                                  + `This changes RPRS capacity only.`
                                  + (impact?.blocker ? `\n\n${impact.blocker}` : ` Practice Engine will not be updated.`)
                                  + `\n\nContinue?`;
                              // eslint-disable-next-line no-restricted-globals
                              if (!window.confirm(msg)) { e.target.value = defaultId || ''; return; }
                            }

                            try {
                              await upsertStaffDefaultTemplate(parseInt(staffId), tid);
                              setStaff(s => s ? { ...s, DefaultCalendarTemplateId: tid } : s);
                            } catch (err) {
                              alert('Failed to update default template: ' + err.message);
                              e.target.value = defaultId || '';   // put the picker back
                            }
                          }}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #BBF7D0', borderRadius: 4, background: '#fff', minWidth: 180 }}>
                          <option value="">— No default —</option>
                          {pickable.map(c => (
                            <option key={c.TemplateId} value={c.TemplateId}>
                              {c.IsGlobal ? '' : '⚙ '}{c.TemplateName} ({c.MonHours}/{c.TueHours}/{c.WedHours}/{c.ThuHours}/{c.FriHours}h)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {!defaultCal && isSuperUser && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#6B7280' }}>
                        No default set — this staffer falls back to dept-scoped globals only.
                      </div>
                    )}
                    {disabledCustoms > 0 && isSuperUser && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#92400E' }}>
                        {disabledCustoms} disabled custom template{disabledCustoms === 1 ? '' : 's'} below
                        {disabledCustoms === 1 ? ' is' : ' are'} not listed here — a disabled template
                        never applies. To use one again, hit <strong>Edit Calendars</strong> and
                        press <strong>Enable</strong> on it, then pick it here.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Not employed today outranks every template — say so plainly rather than
                  badging a template that contributes 0h. */}
              {!employedToday && (
                <div className="sched-cal-info-banner" style={{ background: '#EFF6FF', borderColor: '#BFDBFE', color: '#1E40AF' }}>
                  {staff?.StartDate && toISO(new Date()) < String(staff.StartDate).slice(0, 10)
                    ? <>Not started yet — starts <strong>{String(staff.StartDate).slice(0, 10)}</strong>. Capacity is 0h until then, so no template is in effect.</>
                    : <>No longer employed as of <strong>{String(staff.EndDate).slice(0, 10)}</strong>. Capacity is 0h, so no template is in effect.</>}
                </div>
              )}

              {/* Only claim globals are overridden when an ACTIVE override actually won. */}
              {employedToday && resolved.template?.StaffId != null && (
                <div className="sched-cal-info-banner" style={{ background: '#FFFBEB', borderColor: '#FDE68A', color: '#92400E' }}>
                  A custom override is in effect for this staff member. Global templates are shown
                  for reference but do not apply — except a higher-priority seasonal template,
                  which still takes precedence during its date range.
                </div>
              )}

              {employedToday && resolved.ambiguous && (
                <div className="sched-cal-info-banner" style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#991B1B' }}>
                  ⚠ More than one template is tied at the same priority for this staff member.
                  One wins deterministically, but the others are dead weight — deactivate the
                  ones that no longer apply.
                </div>
              )}

              {/* Custom templates (per-staff) — shown whenever any exist, active or not, so
                  deactivated rows stay auditable instead of vanishing. */}
              {customCals.length > 0 && (
                <>
                  <div className="sched-cal-section-label">Custom Templates</div>
                  <div className="sched-cal-list">
                    {customCals.map(cal => renderCard(cal, isSuperUser))}
                  </div>
                </>
              )}

              {/* Global templates */}
              <div className="sched-cal-section-label" style={customCals.length > 0 ? { marginTop: 16 } : undefined}>
                {hasCustom ? 'Global Templates (reference)' : 'Global Templates (inherited)'}
              </div>
              <div className="sched-cal-list">
                {globalCals.map(cal => renderCard(cal, isSuperUser))}
                {globalCals.length === 0 && (
                  <div className="sched-emp-exp-empty">No global templates configured.</div>
                )}
              </div>

              {/* Add buttons + edit form */}
              {isSuperUser && calEditing && !editingCal && (
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button className="sched-cal-add-btn" style={{ flex: 1 }}
                    onClick={() => setEditingCal(emptyTemplate(parseInt(staffId), false))}>
                    + Add Custom Override
                  </button>
                  <button className="sched-cal-add-btn" style={{ flex: 1 }}
                    onClick={() => setEditingCal(emptyTemplate(parseInt(staffId), true))}>
                    + Add Global Template
                  </button>
                </div>
              )}

              {isSuperUser && renderForm()}
            </div>
          );
        })()}

        {/* ── PTO Tab ── */}
        {profileTab === 'pto' && (() => {
          // Group PTO day-rows into requests by LeaveReqID
          const byReq = {};
          for (const row of ptoData) {
            const key = row.LeaveReqID || `${row.StaffId}_${row.PTODate}`;
            if (!byReq[key]) {
              byReq[key] = {
                leaveReqId: row.LeaveReqID,
                status: row.Status || 'approved',
                requestNotes: row.RequestNotes,
                requestedOn: row.RequestedOn,
                decisionBy: row.DecisionBy,
                leaveType: row.LeaveType,
                totalHours: 0,
                days: [],
              };
            }
            byReq[key].totalHours += (row.Hours || 0);
            const dayISO = (typeof row.PTODate === 'string' ? row.PTODate : '').slice(0, 10);
            byReq[key].days.push({ date: dayISO, hours: row.Hours });
          }
          const allReqs = Object.values(byReq).map(r => {
            const sorted = r.days.map(d => d.date).filter(Boolean).sort();
            r.startDate = sorted[0] || null;
            r.endDate = sorted[sorted.length - 1] || null;
            return r;
          });
          const todayISO = new Date().toISOString().slice(0, 10);
          const filtered = ptoStatusFilter === 'all' ? allReqs : allReqs.filter(r => r.status === ptoStatusFilter);
          const upcoming = filtered.filter(r => (r.endDate || '') >= todayISO).sort((a, b) => {
            if (a.status === 'pending' && b.status !== 'pending') return -1;
            if (b.status === 'pending' && a.status !== 'pending') return 1;
            return (a.startDate || '').localeCompare(b.startDate || '');
          });
          const past = filtered.filter(r => (r.endDate || '') < todayISO).sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));

          const fmtD = iso => {
            if (!iso) return '\u2014';
            const d = new Date(iso + 'T12:00:00');
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          };
          const fmtReqDate = iso => {
            if (!iso) return '\u2014';
            return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          };
          const renderNote = notes => {
            if (!notes || notes === 'New Leave Request' || notes === 'Leave Request') return null;
            return notes.replace(/^New Leave Request\s*[-\u2013]\s*/i, '');
          };

          // Past-dated PTO is excluded: cancelling calls PE's CancelLeaveRequest, which
          // auto-posts a reversing WIP entry. On a day already taken and posted that lands
          // in a closed period, so it needs a PTO admin rather than self-service. Same
          // guard backs the grid's self-cancel popover (SchedulerGrid myPtoLookup.isPast).
          const canCancel = (req) => isMe && req.leaveReqId
            && (req.status === 'pending' || req.status === 'approved')
            && (req.endDate || '') >= todayISO;

          const handleCancel = async (req) => {
            const label = req.status === 'approved' ? 'approved' : 'pending';
            if (!window.confirm(`Cancel this ${label} PTO request (${fmtD(req.startDate)} – ${fmtD(req.endDate)}, ${req.totalHours}h)?`)) return;
            setPtoCancelling(req.leaveReqId);
            try {
              await cancelPTO({ leaveReqId: req.leaveReqId, staffId: parseInt(staffId) });
              const refreshed = await fetchPTO({ staffId: parseInt(staffId) });
              setPtoData(refreshed || []);
            } catch (e) { alert('Cancel failed: ' + e.message); }
            setPtoCancelling(null);
          };

          const handleSubmitRequest = async () => {
            if (!ptoForm.startDate || !ptoForm.endDate) return;
            setPtoSubmitting(true);
            try {
              await requestPTO({ staffId: parseInt(staffId), startDate: ptoForm.startDate, endDate: ptoForm.endDate, notes: ptoForm.notes || '' });
              setPtoForm({ startDate: '', endDate: '', notes: '' });
              setPtoFormOpen(false);
              // No sleep needed: rprsPTO writes the request through to rprs.StaffPTO
              // before responding, so it's queryable the moment this resolves.
              const refreshed = await fetchPTO({ staffId: parseInt(staffId) });
              setPtoData(refreshed || []);
            } catch (e) { alert('Request failed: ' + e.message); }
            setPtoSubmitting(false);
          };

          const renderReqRow = (req) => {
            const isPending = req.status === 'pending';
            const dateRange = req.startDate === req.endDate
              ? fmtD(req.startDate)
              : `${fmtD(req.startDate)} \u2013 ${fmtD(req.endDate)}`;
            const note = renderNote(req.requestNotes);
            return (
              <div key={req.leaveReqId || `${req.startDate}`} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderBottom: '1px solid #F3F4F6',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: isPending ? '#3B82F6' : req.status === 'rejected' ? '#EF4444' : req.status === 'cancelled' ? '#9CA3AF' : '#10B981' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{dateRange}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>{req.totalHours}h</span>
                    <span style={{ fontSize: 10, color: '#9CA3AF' }}>({req.days.length} day{req.days.length !== 1 ? 's' : ''})</span>
                    <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                      background: isPending ? '#DBEAFE' : req.status === 'rejected' ? '#FEE2E2' : req.status === 'cancelled' ? '#F3F4F6' : '#D1FAE5',
                      color: isPending ? '#1D4ED8' : req.status === 'rejected' ? '#991B1B' : req.status === 'cancelled' ? '#6B7280' : '#065F46', marginLeft: 'auto' }}>
                      {isPending ? 'PENDING' : req.status === 'rejected' ? 'REJECTED' : req.status === 'cancelled' ? 'CANCELLED' : 'APPROVED'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    <span>Requested {fmtReqDate(req.requestedOn)}</span>
                    {req.decisionBy && <span>&middot; {req.status === 'rejected' ? 'Rejected' : req.status === 'cancelled' ? 'Cancelled' : 'Approved'} by {req.decisionBy}</span>}
                    {canCancel(req) && (
                      <button onClick={() => handleCancel(req)} disabled={ptoCancelling === req.leaveReqId}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                          color: '#EF4444', padding: 0, marginLeft: 'auto', opacity: ptoCancelling === req.leaveReqId ? 0.5 : 1 }}>
                        {ptoCancelling === req.leaveReqId ? 'Cancelling...' : 'Cancel'}
                      </button>
                    )}
                  </div>
                  {note && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, fontStyle: 'italic' }}>{note}</div>}
                </div>
              </div>
            );
          };

          return (
            <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
              {/* Request PTO form (own profile only) */}
              {isMe && (
                <div style={{ marginBottom: 20 }}>
                  {!ptoFormOpen ? (
                    <button onClick={() => setPtoFormOpen(true)}
                      className="sched-assign-btn" style={{ fontSize: 12 }}>
                      + Request PTO
                    </button>
                  ) : (
                    <div style={{ background: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB', padding: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.deepTeal, marginBottom: 12 }}>New PTO Request</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
                        <div>
                          <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Start Date</label>
                          <input type="date" value={ptoForm.startDate} onChange={e => setPtoForm(p => ({ ...p, startDate: e.target.value }))}
                            style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>End Date</label>
                          <input type="date" value={ptoForm.endDate} onChange={e => setPtoForm(p => ({ ...p, endDate: e.target.value }))}
                            min={ptoForm.startDate || undefined}
                            style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }} />
                        </div>
                        <div style={{ flex: '1 1 160px' }}>
                          <label style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Notes (optional)</label>
                          <input type="text" value={ptoForm.notes} onChange={e => setPtoForm(p => ({ ...p, notes: e.target.value }))}
                            placeholder="e.g. Vacation, Doctor appointment"
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="sched-assign-btn" onClick={handleSubmitRequest}
                          disabled={ptoSubmitting || !ptoForm.startDate || !ptoForm.endDate}
                          style={{ fontSize: 12, opacity: (!ptoForm.startDate || !ptoForm.endDate) ? 0.5 : 1 }}>
                          {ptoSubmitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                        <button onClick={() => { setPtoFormOpen(false); setPtoForm({ startDate: '', endDate: '', notes: '' }); }}
                          style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: '#6B7280' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Leave Balance */}
              {ptoBalanceLoading ? (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16 }}>Loading balance...</div>
              ) : ptoBalance && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Available', hours: ptoBalance.availableHours, days: ptoBalance.availableDays, color: '#059669' },
                    { label: 'Used', hours: ptoBalance.usedHours, days: ptoBalance.usedDays, color: '#6B7280' },
                    { label: 'Pending', hours: ptoBalance.requestHours, days: ptoBalance.requestDays, color: '#3B82F6' },
                    { label: 'Entitlement', hours: ptoBalance.entitlementHours, days: ptoBalance.entitlementDays, color: BRAND.deepTeal },
                  ].map(b => (
                    <div key={b.label} style={{ flex: '1 1 100px', background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', padding: '10px 14px', minWidth: 90 }}>
                      <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{b.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: b.color }}>{b.hours}h</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{b.days} days</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Status filter pills */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
                {['all', 'pending', 'approved', 'rejected', 'cancelled'].map(f => (
                  <button key={f} onClick={() => setPtoStatusFilter(f)}
                    style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer',
                      background: ptoStatusFilter === f ? (f === 'pending' ? '#3B82F6' : f === 'rejected' ? '#EF4444' : BRAND.deepTeal) : '#F3F4F6',
                      color: ptoStatusFilter === f ? '#fff' : '#6B7280', textTransform: 'capitalize', transition: 'all 0.15s' }}>
                    {f}
                  </button>
                ))}
              </div>

              {/* Upcoming */}
              <h3 className="sched-emp-panel-title">Upcoming PTO ({upcoming.length})</h3>
              {upcoming.length === 0 ? (
                <div className="sched-emp-exp-empty">No upcoming PTO requests.</div>
              ) : (
                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 24 }}>
                  {upcoming.map(renderReqRow)}
                </div>
              )}

              {/* Past */}
              {past.length > 0 && (
                <>
                  <h3 className="sched-emp-panel-title" style={{ marginTop: 8 }}>Past PTO ({past.length})</h3>
                  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden', opacity: 0.7 }}>
                    {past.map(renderReqRow)}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Non-Billable Tab (Stage 28I) ── */}
        {profileTab === 'nonbill' && (
          <NonBillProfilePanel staffId={staffId ? parseInt(staffId, 10) : null} />
        )}

        {/* ── Profile Tab (Certifications, Interests, Skills) ── */}
        {profileTab === 'profile' && (
          <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
            {/* Certifications */}
            <h3 className="sched-emp-panel-title">Certifications</h3>
            {certifications.length === 0 ? (
              <div className="sched-emp-exp-empty">No certifications recorded. These can be added by staff or admin.</div>
            ) : (
              <div className="sched-emp-profile-list">
                {certifications.map(c => (
                  <div key={c.CertId} className="sched-emp-profile-tag">
                    <span className="sched-emp-profile-tag-name">{c.CertName}</span>
                    {c.CertState && <span className="sched-emp-profile-tag-detail">{c.CertState}</span>}
                    {c.ExpiryDate && <span className="sched-emp-profile-tag-detail">Exp: {fmtDate(c.ExpiryDate)}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Niche Preferences */}
            <div data-tour="niche-prefs">
            <h3 className="sched-emp-panel-title" style={{ marginTop: 20 }}>Niche Preferences</h3>
            <p style={{ fontSize: 12, color: '#6B7280', marginTop: -4, marginBottom: 12 }}>
              Rank up to 5 niches this staff member prefers to work in. Used to weight recommendations.
            </p>
            {/* Ranked slots 1-5 */}
            {[1, 2, 3, 4, 5].map(rank => {
              const pref = nichePrefs.find(p => p.rank === rank);
              return (
                <div key={rank} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', width: 16, textAlign: 'right' }}>#{rank}</span>
                  <select
                    value={pref?.interestTag || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setNichePrefs(prev => {
                        const next = prev.filter(p => p.rank !== rank);
                        if (val) next.push({ interestTag: val, rank });
                        return next.sort((a, b) => a.rank - b.rank);
                      });
                    }}
                    style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, color: pref ? '#374151' : '#9CA3AF' }}
                  >
                    <option value="">— not set —</option>
                    {ALL_NICHES.filter(n => !nichePrefs.some(p => p.interestTag === n && p.rank !== rank))
                      .map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <button
                className="sched-assign-btn"
                style={{ fontSize: 12, padding: '6px 14px' }}
                disabled={nicheSaving}
                onClick={async () => {
                  setNicheSaving(true);
                  try {
                    await upsertInterest(parseInt(staffId), nichePrefs);
                    const intData = await fetchInterests(parseInt(staffId));
                    const refreshed = (intData || [])
                      .filter(i => i.Rank != null)
                      .sort((a, b) => a.Rank - b.Rank)
                      .map(i => ({ interestTag: i.InterestTag, rank: i.Rank }));
                    setNichePrefs(refreshed);
                  } catch (e) {
                    alert('Save failed: ' + e.message);
                  } finally {
                    setNicheSaving(false);
                  }
                }}
              >
                {nicheSaving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
            </div>{/* /niche-prefs */}

            {/* Skills */}
            <h3 className="sched-emp-panel-title" style={{ marginTop: 20 }}>Skills</h3>
            {skills.length === 0 ? (
              <div className="sched-emp-exp-empty">No skills recorded.</div>
            ) : (
              <div className="sched-emp-profile-list">
                {skills.map(s => (
                  <div key={s.SkillId} className="sched-emp-profile-tag">
                    <span className="sched-emp-profile-tag-name">{s.SkillName}</span>
                    {s.SkillLevel && <span className="sched-emp-profile-tag-detail">{s.SkillLevel}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* PI Assessment */}
            <h3 className="sched-emp-panel-title" style={{ marginTop: 20 }}>Behavioral Assessment (PI)</h3>
            {(() => {
              const piAssessments = assessments.filter(a => a.AssessmentType === 'PI');
              if (piAssessments.length === 0) return (
                <div className="sched-emp-exp-empty">No PI assessment data available.</div>
              );
              return piAssessments.map(pi => {
                let factors = null;
                try { factors = JSON.parse(pi.ResultDetail); } catch { /* ignore */ }
                const profile = PI_PROFILES[pi.ResultLabel];
                const catColor = profile ? PI_CATEGORY_COLORS[profile.category] : null;
                const FACTOR_LABELS = { A: 'Dominance', B: 'Extraversion', C: 'Patience', D: 'Formality', E: 'Objectivity' };
                return (
                  <div key={pi.AssessmentId} style={{ background: '#F9FAFB', borderRadius: 10, padding: 14, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 14px', borderRadius: 12,
                        background: BRAND.deepTeal, color: '#fff', fontSize: 14, fontWeight: 700,
                      }}>
                        {pi.ResultLabel}
                      </span>
                      {catColor && (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                          background: catColor.bg, color: catColor.text,
                        }}>
                          {profile.category}
                        </span>
                      )}
                    </div>
                    {profile && (
                      <p style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.5, marginBottom: 8 }}>
                        {profile.desc}
                      </p>
                    )}
                    {factors && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {['A', 'B', 'C', 'D', 'E'].map(f => {
                          const val = factors[`factor${f}`];
                          if (val == null) return null;
                          const isHigh = val > 0.5;
                          const isLow = val < -0.5;
                          return (
                            <span key={f} title={FACTOR_LABELS[f]} style={{
                              background: isHigh ? '#DCFCE7' : isLow ? '#FEE2E2' : '#F3F4F6',
                              color: isHigh ? '#166534' : isLow ? '#991B1B' : '#6B7280',
                              padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            }}>
                              {FACTOR_LABELS[f]}: {val > 0 ? '+' : ''}{val.toFixed(1)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}

            {/* Working Genius (stub) */}
            <h3 className="sched-emp-panel-title" style={{ marginTop: 20 }}>Working Genius</h3>
            <div className="sched-emp-exp-empty" style={{ color: '#9CA3AF' }}>
              No assessment data yet. Working Genius results will appear here once assessments are completed.
            </div>

            {/* Staff details */}
            <h3 className="sched-emp-panel-title" style={{ marginTop: 20 }}>Staff Details</h3>
            <div className="sched-emp-details">
              <div className="sched-emp-detail-row"><span>Start Date</span><span>{fmtDate(staff.StartDate)}</span></div>
              <div className="sched-emp-detail-row"><span>Staff Code</span><span>{staff.StaffCode || '\u2014'}</span></div>
              <div className="sched-emp-detail-row"><span>PE Staff Index</span><span>{staff.PEStaffIndex || '\u2014'}</span></div>
              <div className="sched-emp-detail-row"><span>Status</span><span>{staff.IsActive ? 'Active' : 'Inactive'}</span></div>
            </div>
          </div>
        )}

        {/* ── Schedule Tab ── */}
        {profileTab === 'schedule' && (
          <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
            <h3 className="sched-emp-panel-title">Upcoming Assignments ({upcomingJobs.length})</h3>
            {upcomingJobs.length === 0 ? (
              <div className="sched-emp-exp-empty">No assignments in the next 4 weeks.</div>
            ) : (
              <div className="sched-emp-job-list">
                {upcomingJobs.map(j => (
                  <div key={j.JobId} className="sched-emp-job-card">
                    <div className="sched-emp-job-client">{j.ClientName}</div>
                    <div className="sched-emp-job-name">{j.JobName}</div>
                    <div className="sched-emp-job-hours">{Math.round(j.totalHours * 100) / 100}h</div>
                  </div>
                ))}
              </div>
            )}

            <button className="sched-assign-btn" style={{ marginTop: 16 }}
              onClick={() => {
                // Focus the grid on this staffer (people view, current 5-week window).
                sessionStorage.setItem('gridViewContext', JSON.stringify({
                  staffIds: [staff.StaffId],
                  scrollToStaffId: staff.StaffId,
                  tab: 'calendar',
                  weekOffset: 0,
                  zoom: 5,
                  message: `Showing ${staff.StaffName}'s schedule`,
                }));
                navigate('/schedule/grid');
              }}>
              View in Scheduler
            </button>
          </div>
        )}
      </div>

      {/* Experience Ranking Modal */}
      {rankingModal && (
        <div className="sched-mgr-modal-backdrop" onClick={() => setRankingModal(null)}>
          <div className="sched-mgr-modal" style={{ width: 600, maxHeight: '80vh' }} onClick={ev => ev.stopPropagation()}>
            <div className="sched-mgr-modal-header">
              <h3>{rankingModal.dimensionName}</h3>
              <button className="sched-mgr-sidebar-close" onClick={() => setRankingModal(null)}>&times;</button>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {rankingModal.dimension} experience &middot; {rankingData.length} staff ranked
            </div>
            <ExperienceRankingList
              rows={rankingData}
              loading={rankingLoading}
              highlightStaffId={staffId}
              onStaffClick={(sid) => { setRankingModal(null); navigate(`/schedule/employee/${sid}`); }}
            />
          </div>
        </div>
      )}

      {showHistory && (
        <ScheduleHistoryModal
          staffId={staff.StaffId}
          staffName={staff.Name}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
